import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form-latest';
import StepLinkList from '@/components/UI/StepLinkList';
import { SamplePlanTable } from '../SamplePlanTable';
import { SamplePlanTableProvider } from '../SamplePlanTableProvider';
import { RouteService } from '@/support/RouteService.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { errorToast, successToast } from '@/helpers.tsx';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError.ts';
import DateTimePicker from '@/components/UI/FormFields/Latest/DateTimePicker';
import Button from '@/components/UI/Button';
import Loading from '@/components/Loading/Loading';
import { StudyApiId } from '@/model/Study.model.ts';
import { GroupApiId, TreatmentGroup } from '@/model/TreatmentGroup.model';
import {
  CollectionMethod,
  CollectionMethodType,
  GroupTarget,
  PlannedSampleSheetCreateV1,
  RegisteredAnimalTarget,
  SampleDefinitionApiId,
  SamplingSheetBaseColumn,
  SamplingSheetColumnId,
} from '@/model/SamplePlan.model';
import { MetadataGlossaryApiId } from '@/model/Metadata.model';
import { Color, ISODateTime } from '@/model/Common.model';
import { TeamGlossary } from '@/model/Glosary.model';
import { DateInputUtils, DateUtils, TimeUtils } from '@/utils/Date.utils.ts';
import Banner from '@/components/UI/Banner';
import { AnimalApiId } from '@/model/Animal.model.ts';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model.ts';
import { _isEmpty, _isNil, _isNotBlank, _notNil } from '@/littledash.ts';
import Header from '@/components/UI/Header';
import CollectionMethodSelector from './CollectionMethodSelector';
import SamplingSheetColumnSelector from './SamplingSheetColumnSelector';
import {
  BaseColumns,
  buildRegisteredAnimalsMap,
  buildSampleDefinitionsMap,
  calculateTotalSamplesFromMap,
  defaultSelectedColumns,
  generateSamplesPlanRoute,
  generateStudySamplesRoute,
  getRequiredColumns,
  hasValidExecutionState,
  isLoadingRequiredData,
} from './ExecuteSamples.utils';

interface FormData {
  date: string;
  comments: string;
  collectionMethod: CollectionMethodType;
  sampleSheetName: string;
}

interface LocationState {
  sampleDefinitionIds?: Array<SampleDefinitionApiId>;
  selectedAnimals?: Array<AnimalApiId>;
  studyApiId?: StudyApiId;
  animalIdDefinitionMap?: Map<AnimalApiId, AnimalDefinitionApiId>;
}

const ExecuteSamplesCollectStepV2: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: studyId = '' } = useParams<{ id: string }>();
  const defaultDate: ISODateTime = DateUtils.createDateTimeInUserTimezone(
    DateUtils.dateNow(),
    TimeUtils.currentTimeInUserTimezone()
  );

  const defaultSampleSheetName = `sample-sheet-${DateUtils.dateNow()}`;

  const { studyApiId, selectedAnimals, sampleDefinitionIds, animalIdDefinitionMap } = useMemo(() => {
    const stateData = location.state as LocationState | undefined;

    const studyApiId = stateData?.studyApiId as StudyApiId;
    const selectedAnimals = _notNil(stateData?.selectedAnimals)
      ? new Set(stateData?.selectedAnimals)
      : new Set<AnimalApiId>();
    const sampleDefinitionIds = stateData?.sampleDefinitionIds ?? [];
    const animalIdDefinitionMap = stateData?.animalIdDefinitionMap ?? new Map<AnimalApiId, AnimalDefinitionApiId>();

    return { studyApiId, selectedAnimals, sampleDefinitionIds, animalIdDefinitionMap };
  }, [location.state]);

  // Generate back link to sample table
  const backLink = useMemo(() => generateStudySamplesRoute(studyId), [studyId]);
  const samplesPlanRoute = useMemo(() => generateSamplesPlanRoute(studyId), [studyId]);

  // Step form configuration
  const steps = useMemo(
    () => [
      {
        title: 'Select animals',
        complete: true,
      },
      {
        title: 'Collect samples',
        complete: false,
      },
    ],
    []
  );

  const currentStep = 2;

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      date: DateInputUtils.toLocalDateTime(defaultDate),
      comments: '',
      collectionMethod: CollectionMethod.quick_collect,
      sampleSheetName: defaultSampleSheetName,
    },
  });

  const { watch, formState, reset } = formMethods;
  const { isValid, errors } = formState;
  const collectionMethod = watch('collectionMethod');

  const [selectedColumns, setSelectedColumns] = useState<Set<SamplingSheetColumnId>>(new Set(defaultSelectedColumns));

  const handleColumnChange = useCallback((columnId: SamplingSheetColumnId, selected: boolean) => {
    setSelectedColumns((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(columnId);
      } else {
        newSet.delete(columnId);
      }
      return newSet;
    });
  }, []);

  const { response: groupsResponse, loading: groupsLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.groups.list',
    path: { id: studyId },
    options: { invokeOnInit: true },
  });

  const { response: sampleMetadataGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: false,
      filter_type: 'sample_meta',
    },
    invokeOnInit: true,
  });

  const { response: sampleGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'samples',
      perPage: -1,
      order: 'asc',
      sort: 'title',
    },
    invokeOnInit: true,
    options: { onError: { slug: 'samples-glossary-load' } },
  });

  const {
    loading: samplePlanLoading,
    response: samplePlanResponse,
    invoke: loadSamplePlan,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples',
    invokeOnInit: false,
  });

  const {
    loading: registeredAnimalsLoading,
    response: registeredAnimalsResponse,
    invoke: loadRegisteredAnimals,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples/animals',
    invokeOnInit: false,
  });

  // Load data when studyApiId is available
  // Note: loadSamplePlan and loadRegisteredAnimals are intentionally excluded from dependencies
  // as they are stable function references from useApiHook but would cause re-renders if included
  useEffect(() => {
    if (_notNil(studyApiId)) {
      loadSamplePlan({ path: { studyApiId } });
      loadRegisteredAnimals({
        path: { studyApiId },
        query: {
          definitions: sampleDefinitionIds,
          collection_type: 'collected',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyApiId, sampleDefinitionIds]);

  const tableData = useMemo(() => {
    if (
      groupsResponse?.type === 'success' &&
      registeredAnimalsResponse?.type === 'success' &&
      samplePlanResponse?.type === 'success'
    ) {
      const groups = (
        groupsResponse.body as {
          data: Array<TreatmentGroup>;
        }
      ).data.map<GroupTarget>((group) => ({
        api_id: group.api_id as GroupApiId,
        name: group.name,
        no: group.no,
        color: group.color as Color,
        max_subjects: group.max_subjects,
      }));

      // Filter registered animals to only include selected ones
      const registeredAnimalsData = registeredAnimalsResponse?.body?.data ?? [];
      const registered_animals = registeredAnimalsData
        .filter((animal) => selectedAnimals.has(animal.api_id))
        .map<RegisteredAnimalTarget>((animal) => ({
          api_id: animal.api_id,
          name: animal.name,
          study_group_api_id: _notNil(animal.study_group) ? animal.study_group.api_id : null,
        }));

      // Filter sample definitions to only include selected ones
      const filteredSampleDefinitions = samplePlanResponse.body.definitions.filter((def) =>
        sampleDefinitionIds.includes(def.api_id)
      );

      return {
        groups,
        registered_animals,
        sample_definitions: filteredSampleDefinitions,
        planned_animals: [],
      };
    }
    return null;
  }, [groupsResponse, registeredAnimalsResponse, samplePlanResponse, sampleDefinitionIds, selectedAnimals]);

  const sampleDefinitionsMap = useMemo(() => {
    if (registeredAnimalsResponse?.type !== 'success' || _isNil(tableData)) {
      return {};
    }
    const registeredAnimalsMap = buildRegisteredAnimalsMap(registeredAnimalsResponse);
    return buildSampleDefinitionsMap(
      selectedAnimals,
      tableData.sample_definitions,
      registeredAnimalsMap,
      animalIdDefinitionMap
    );
  }, [selectedAnimals, tableData, registeredAnimalsResponse, animalIdDefinitionMap]);

  const totalSamples = useMemo(() => {
    if (_isNil(tableData)) {
      return 0;
    }
    return calculateTotalSamplesFromMap(tableData.sample_definitions, sampleDefinitionsMap);
  }, [tableData, sampleDefinitionsMap]);

  const hasCalculateCollectionTime = useMemo(() => {
    if (_isNil(tableData)) {
      return false;
    }
    return tableData.sample_definitions.some((def) => def.calculate_collection_time === true);
  }, [tableData]);

  const requiredColumns = useMemo(() => getRequiredColumns(hasCalculateCollectionTime), [hasCalculateCollectionTime]);

  // Compute effective selected columns as union of user selections and required columns.
  const effectiveSelectedColumns = useMemo(() => {
    const effective = new Set(selectedColumns);
    requiredColumns.forEach((col) => effective.add(col));
    return effective;
  }, [selectedColumns, requiredColumns]);

  const { loading: isCreating, invoke: executeCreateSamples } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/execute',
    path: { studyApiId },
    invokeOnInit: false,
    options: {
      onError: { toast: true },
    },
  });

  const { loading: isCreatingSampleSheet, invoke: executeCreateSampleSheet } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/datatables/sample-sheet',
    path: { studyApiId },
    invokeOnInit: false,
    options: {
      onError: { toast: true, slug: 'create-sample-sheet' },
    },
  });

  const handleSaveAndFinish = useCallback(
    (formData: FormData) => {
      if (_isNil(studyApiId) || _isEmpty(selectedAnimals)) {
        errorToast('No animals selected. Please go back and select animals.');
        return;
      }

      if (!_isNotBlank(formData.date)) {
        errorToast('Please select a date for sample collection.');
        return;
      }

      executeCreateSamples({
        path: { studyApiId },
        body: {
          sample_definitions: sampleDefinitionsMap,
          date: DateInputUtils.localDateTimeToISODateTime(formData.date),
          comments: _isNotBlank(formData.comments) ? formData.comments : null,
        },
      })
        .then(() => {
          successToast('Samples collected successfully!');
          navigate(samplesPlanRoute, { replace: true });
          navigate(backLink);
        })
        .catch((cause) => {
          ExceptionHandler.captureException(
            new InVivoError('Failed to save sample collection', {
              cause,
              slug: 'sample-collection-quick-collect-failed',
              context: {
                studyApiId: String(studyApiId),
                sampleDefinitions: JSON.stringify(sampleDefinitionsMap),
              },
            })
          );
          errorToast('Failed to save sample collection. Please try again.');
        });
    },
    [studyApiId, selectedAnimals, executeCreateSamples, navigate, samplesPlanRoute, backLink, sampleDefinitionsMap]
  );

  const handleCreateSampleSheet = useCallback(
    (formData: FormData) => {
      if (_isNil(studyApiId) || _isEmpty(selectedAnimals)) {
        errorToast('No animals selected. Please go back and select animals.');
        return;
      }

      if (!_isNotBlank(formData.sampleSheetName)) {
        errorToast('Please enter a sample sheet name.');
        return;
      }

      // Separate selected columns into base columns and metadata columns.
      // effectiveSelectedColumns includes both user selections and required columns.
      const { baseColumns, metadataColumns } = Array.from(effectiveSelectedColumns).reduce<{
        baseColumns: Array<SamplingSheetBaseColumn>;
        metadataColumns: Array<MetadataGlossaryApiId>;
      }>(
        (acc, id) => {
          if (id in BaseColumns) {
            acc.baseColumns.push(id as SamplingSheetBaseColumn);
          } else {
            acc.metadataColumns.push(id as MetadataGlossaryApiId);
          }
          return acc;
        },
        { baseColumns: [], metadataColumns: [] }
      );

      const payload: PlannedSampleSheetCreateV1 = {
        name: formData.sampleSheetName,
        sample_definitions: sampleDefinitionsMap,
        base_columns: baseColumns,
        metadata_columns: metadataColumns,
      };

      executeCreateSampleSheet({
        path: { studyApiId },
        body: payload,
      })
        .then((response) => {
          successToast('Sample sheet created successfully');
          // Replace the form entry with the samples plan view so back navigation
          // from the destination lands on samples/?tab=plan instead of the form.
          navigate(samplesPlanRoute, { replace: true });
          const dataTableApiId = response?.body?.id;
          if (_notNil(dataTableApiId)) {
            navigate(
              RouteService.web({
                route: 'studies.dataTables.show',
                path: { id: studyId, dataTableApiId },
                query: { type: 'sample_sheet' },
              }).route
            );
          }
        })
        .catch((cause) => {
          ExceptionHandler.captureException(
            new InVivoError('Failed to create sample sheet', {
              cause,
              slug: 'sample-sheet-creation-failed',
              context: {
                studyApiId: String(studyApiId),
                sampleDefinitions: JSON.stringify(sampleDefinitionsMap),
              },
            })
          );
          errorToast('Failed to create sample sheet. Please try again.');
        });
    },
    [
      studyApiId,
      studyId,
      selectedAnimals,
      effectiveSelectedColumns,
      executeCreateSampleSheet,
      navigate,
      samplesPlanRoute,
      sampleDefinitionsMap,
    ]
  );

  const handleFormSubmit = useCallback(
    (formData: FormData) => {
      if (formData.collectionMethod === CollectionMethod.quick_collect) {
        handleSaveAndFinish(formData);
      } else {
        handleCreateSampleSheet(formData);
      }
    },
    [handleSaveAndFinish, handleCreateSampleSheet]
  );

  const handleCollectionMethodChange = useCallback(
    (value: string) => {
      reset({
        date: DateInputUtils.toLocalDateTime(defaultDate),
        comments: '',
        collectionMethod: value as CollectionMethodType,
        sampleSheetName: defaultSampleSheetName,
      });
    },
    [reset, defaultDate, defaultSampleSheetName]
  );

  const handleGoBack = useCallback(() => {
    navigate(
      RouteService.web({
        route: 'studies.samples.execute',
        path: { id: studyId },
      }).route,
      {
        state: {
          sampleDefinitionIds,
          studyApiId,
        },
      }
    );
  }, [navigate, studyId, sampleDefinitionIds, studyApiId]);

  // Show loading while data is being fetched
  if (isLoadingRequiredData(groupsLoading, samplePlanLoading, registeredAnimalsLoading, tableData)) {
    return <Loading />;
  }

  // If no data was provided in state, redirect back to sampling plan
  if (!hasValidExecutionState(studyApiId, sampleDefinitionIds, selectedAnimals)) {
    return (
      <div data-test-component="ExecuteSamplesCollectStepV2" data-test-element="error-state">
        <div className="ph4 pv3">
          <p>
            No sample collection data found. Please return to the sampling plan to select samples and animals to
            collect.
          </p>
          <button type="button" className="btn plain" onClick={() => navigate(backLink)}>
            Return to Sampling Plan
          </button>
        </div>
      </div>
    );
  }

  const isSubmitDisabled = isCreating || isCreatingSampleSheet || _isEmpty(selectedAnimals) || !isValid;

  const getSubmitButtonText = () => {
    if (collectionMethod === CollectionMethod.quick_collect) {
      return isCreating ? 'Collecting...' : 'Collect';
    }
    return isCreatingSampleSheet ? 'Creating...' : 'Create';
  };

  return (
    <FormProvider {...formMethods}>
      <div data-test-component="ExecuteSamplesCollectStepV2" data-test-element="container">
        <form onSubmit={formMethods.handleSubmit(handleFormSubmit)}>
          <div className="ph4 pb3">
            <div className="w-100 pb4">
              <Header mainHeaderText="Collect Samples" />
              <StepLinkList style={{ width: '305px' }} steps={steps} activeStep={currentStep} />
            </div>

            {_notNil(tableData) && (
              <div className="mb4" data-test-element="review-section">
                <h3 className="fw5 f5 pb3" data-testid="review-title">
                  Review samples for collection
                </h3>
                <SamplePlanTableProvider
                  sample_definitions={tableData.sample_definitions}
                  groups={tableData.groups}
                  registered_animals={tableData.registered_animals}
                  planned_animals={tableData.planned_animals}
                  metadata_glossary={sampleMetadataGlossaryResponse?.body ?? []}
                  sample_glossary={(sampleGlossaryResponse?.body as { data?: Array<TeamGlossary> })?.data ?? []}
                  displaySampleCounts={true}
                >
                  <SamplePlanTable />
                </SamplePlanTableProvider>
              </div>
            )}

            <h3 className="fw5 f5 pb3" data-testid="collection-method-title">
              Select sample collection method
            </h3>

            <div className="flex mb4 mw8" data-test-element="collection-method-layout">
              <div className="w-40 pr3" data-test-element="selector-column">
                <div className="ba b--black-10 br2 pa3 bg-white h-100">
                  <CollectionMethodSelector value={collectionMethod} onChange={handleCollectionMethodChange} />
                </div>
              </div>

              <div className="w-60" data-test-element="form-column">
                <div className="ba b--black-10 br2 pa4 bg-white h-100">
                  {collectionMethod === CollectionMethod.quick_collect && (
                    <>
                      {totalSamples > 0 && (
                        <Banner info className="mb4" dismiss={false} data-testid="sample-count-banner">
                          <p className="lh-copy">
                            <strong className="mr1">
                              {totalSamples} sample{totalSamples === 1 ? '' : 's'}
                            </strong>
                            will be collected with the following data.
                          </p>
                        </Banner>
                      )}

                      <div className="mb4">
                        <DateTimePicker
                          name="date"
                          label="Collection Date & Time"
                          required={true}
                          includeTime={true}
                          testId="date-picker"
                          data-test-element="date-picker"
                        />
                      </div>

                      <div className="mb2">
                        <label htmlFor="comments" data-testid="comments-label">
                          Comments
                        </label>
                        <textarea
                          {...formMethods.register('comments')}
                          id="comments"
                          className="input-reset ba b--black-10 pa2 mb2 db w-100 br2"
                          rows={4}
                          data-test-element="comments"
                          data-testid="comments-textarea"
                        />
                      </div>
                    </>
                  )}

                  {collectionMethod === CollectionMethod.sampling_sheet && (
                    <>
                      {totalSamples > 0 && (
                        <Banner info className="mb4" dismiss={false} data-testid="sample-sheet-count-banner">
                          <p className="lh-copy">
                            <strong className="mr1">
                              {totalSamples} sample{totalSamples === 1 ? '' : 's'}
                            </strong>
                            to be collected after creating sampling sheet.
                          </p>
                        </Banner>
                      )}

                      <div className="mb4">
                        <label htmlFor="sampleSheetName" className="db mb2" data-testid="sample-sheet-name-label">
                          Sampling sheet name (required)
                        </label>
                        <input
                          {...formMethods.register('sampleSheetName', {
                            required: 'Sample sheet name is required',
                            maxLength: {
                              value: 255,
                              message: 'Sample sheet name must be 255 characters or less',
                            },
                          })}
                          id="sampleSheetName"
                          type="text"
                          className="input-reset ba b--black-10 pa2 mb0 db w-100 br2"
                          data-test-element="sample-sheet-name"
                          data-testid="sample-sheet-name-input"
                        />
                        {_notNil(errors.sampleSheetName) && (
                          <small className="red db" data-test-element="error">
                            {errors.sampleSheetName.message}
                          </small>
                        )}
                      </div>

                      <SamplingSheetColumnSelector
                        sampleMetadata={sampleMetadataGlossaryResponse?.body ?? []}
                        selectedColumns={effectiveSelectedColumns}
                        requiredColumns={requiredColumns}
                        hasCalculateCollectionTime={hasCalculateCollectionTime}
                        onChange={handleColumnChange}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex" data-test-element="actions">
              <Button primary submit disabled={isSubmitDisabled} className="mr3" testKey="submit">
                {getSubmitButtonText()}
              </Button>
              <Button plain onClick={handleGoBack} disabled={isCreating} testKey="back">
                Back
              </Button>
            </div>
          </div>
        </form>
      </div>
    </FormProvider>
  );
};

export default ExecuteSamplesCollectStepV2;
